import { useCallback } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { stringifySync } from '@bingads-webui/safe-json';
import { parseFileName } from '@bingads-webui-campaign-react/upload-file-picker';
import {
  filterFilesByExtensions,
  createFolderHierarchyMap,
  createFoldersByHierarchy,
  createRateLimitedAssetUploadPromises,
  useReadAsset,
  useValidateAsset,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

export const NO_VALID_FILES_ERROR = 'novalidfileserror';
export const ASSET_LIMIT_EXCEEDED_ERROR = 'toomanyassets';

export const useUploadFolder = ({
  assetUploadLimit = 100,
  createFolderAsync,
  currentFolderId,
  uploadAssetAsync,
  supportedExtensions,
  getCustomizedHandlerOptions,
  onUploadError,
  onUploadStart,
  onUploadPartialComplete,
  onUploadComplete,
  clearInputFile,
  isUploadImageUpscaleEnabled,
  logger,
}) => {
  const { readFileAsync } = useReadAsset();
  const { validateFileAsync } = useValidateAsset();

  const validateAssetAsync = useCallback((obj = {}) => validateFileAsync({
    ...obj,
    isUploadImageUpscaleEnabled,
  }).catch((e) => {
    if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
      logger.logTrace(`Add Media from folder per file - SizeValidationError: ${stringifySync(e)}`);
      logger.logTrace(`Add Media from folder per file - SizeValidationError - ImageSource: ${stringifySync(obj.dataURL)}`);
    }
    throw e;
  }), [isUploadImageUpscaleEnabled, logger, validateFileAsync]);

  const onFolderRead = useCallback((event) => {
    const { files } = event.target;
    const acceptedFiles = filterFilesByExtensions({ files, supportedExtensions });
    const customizedFilesOptions = _.map(acceptedFiles, (file) => {
      const { nameWithoutExt, ext } = parseFileName(file.name);

      const passedOptions = getCustomizedHandlerOptions({
        nameWithoutExt,
        ext,
      });

      return passedOptions;
    });

    if (acceptedFiles.length === 0) {
      onUploadError(NO_VALID_FILES_ERROR);

      return Promise.resolve();
    }

    if (acceptedFiles.length > assetUploadLimit) {
      onUploadError(ASSET_LIMIT_EXCEEDED_ERROR);

      return Promise.resolve();
    }

    // should create all folders under selected folder regardless of whether or not
    // folder contains valid assets
    const foldersToCreate = createFolderHierarchyMap(files);
    const uploadFolderInfo = {
      totalSize: _.reduce(acceptedFiles, (memo, { size }) => memo + size, 0),
      rootFolderName: _.keys(foldersToCreate)[0],
      acceptedAssets: acceptedFiles,
    };

    const folderCreationErrors = [];
    const assetsSkippedCreationDueToFolderError = [];
    const assetUploadErrors = [];

    onUploadStart(uploadFolderInfo);
    return createFoldersByHierarchy({
      folderMap: foldersToCreate,
      createFolderAsync,
      folderCreationErrors,
      currentFolderId,
    }).then((creationPromises) => {
      const assetCreationPromise = createRateLimitedAssetUploadPromises({
        customizedFilesOptions,
        acceptedAssets: acceptedFiles,
        folderIdPromises: creationPromises,
        validateAssetAsync,
        uploadAssetAsync,
        assetsSkippedUpload: assetsSkippedCreationDueToFolderError,
        assetUploadErrors,
        readAssetAsync: readFileAsync,
      });
      return assetCreationPromise
        .then(() => {
          if (assetUploadErrors.length > 0 || folderCreationErrors.length > 0) {
            onUploadPartialComplete({
              assetUploadErrors,
              folderCreationErrors,
            });
          } else {
            onUploadComplete();
          }
        })
        .catch((e) => {
          onUploadError(e);
        });
    }).finally(() => {
      clearInputFile(event);
    });
  }, [clearInputFile, supportedExtensions, assetUploadLimit, onUploadStart, createFolderAsync, currentFolderId, getCustomizedHandlerOptions, onUploadError, validateAssetAsync, uploadAssetAsync, readFileAsync, onUploadPartialComplete, onUploadComplete]);

  return {
    onFolderRead,
  };
};
